import { useCallback, useEffect, useRef, useState } from "react";
import {
  ExternalLink,
  Globe,
  Image as ImageIcon,
  Loader2,
  Save,
  Upload,
} from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import AdminLayout from "./AdminLayout";
import { Card } from "./_shared";

const BACKEND = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "");
const resolve = (url) => {
  if (!url) return null;
  if (/^https?:\/\//i.test(url)) return url;
  if (url.startsWith("/api/")) return `${BACKEND}${url}`;
  return url;
};
const TWITTER_CARDS = [
  { value: "summary", label: "summary (small image)" },
  { value: "summary_large_image", label: "summary_large_image (recommended)" },
];

const ROBOTS_PRESETS = [
  { value: "index, follow", label: "index, follow (production default)" },
  { value: "noindex, nofollow", label: "noindex, nofollow (hide from search)" },
  { value: "index, nofollow", label: "index, nofollow" },
];

function Field({ label, hint, children }) {
  return (
    <label className="block">
      <span className="text-xs font-medium text-slate-700">{label}</span>
      {children}
      {hint && <p className="text-[11px] text-slate-500 mt-1">{hint}</p>}
    </label>
  );
}

export default function AdminSiteMeta() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [meta, setMeta] = useState({});
  const [uploading, setUploading] = useState({ favicon: false, og_image: false });
  const faviconInputRef = useRef(null);
  const ogInputRef = useRef(null);

  // Mirror `meta` into a ref so onSave is bulletproof against stale closures
  // when the user clicks the AdminLayout top-right Save button immediately
  // after an upload that just resolved (the actions slot may capture an
  // earlier render's `meta`).
  const metaRef = useRef(meta);
  useEffect(() => {
    metaRef.current = meta;
  }, [meta]);

  const set = (k) => (e) =>
    setMeta((m) => ({ ...m, [k]: e.target.value }));

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/site-meta");
      setMeta(data || {});
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err?.response?.data?.detail) ||
          "Failed to load site meta.",
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const onSave = async (e) => {
    e?.preventDefault?.();
    setSaving(true);
    try {
      const { data } = await api.put("/admin/site-meta", metaRef.current);
      setMeta(data || {});
      toast.success("Site meta saved — reload tabs to see changes propagate.");
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err?.response?.data?.detail) ||
          "Failed to save site meta.",
      );
    } finally {
      setSaving(false);
    }
  };

  const onUpload = (kind) => async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading((u) => ({ ...u, [kind]: true }));
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/site-meta/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const field = kind === "favicon" ? "favicon_url" : "og_image_url";
      setMeta((m) => ({ ...m, [field]: data.url }));
      toast.success(
        `${kind === "favicon" ? "Favicon" : "Open Graph image"} uploaded — remember to Save.`,
      );
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err?.response?.data?.detail) ||
          "Upload failed.",
      );
    } finally {
      setUploading((u) => ({ ...u, [kind]: false }));
      // Reset the input so picking the same file again still triggers onChange.
      if (kind === "favicon" && faviconInputRef.current) {
        faviconInputRef.current.value = "";
      }
      if (kind === "og_image" && ogInputRef.current) {
        ogInputRef.current.value = "";
      }
    }
  };

  if (loading) {
    return (
      <AdminLayout title="Site meta & SEO" subtitle="Loading…">
        <div className="flex items-center gap-2 text-sm text-slate-500">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading site meta…
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout
      title="Site meta & SEO"
      subtitle="Page title, meta description, favicon, social-share previews and search-engine settings. Applied on every visitor's page load."
      actions={
        <button
          type="button"
          onClick={onSave}
          disabled={saving}
          className="inline-flex items-center gap-1.5 rounded-lg bg-emerald-600 px-3 py-2 text-xs font-medium text-white hover:bg-emerald-700 disabled:opacity-60"
          data-testid="site-meta-save"
        >
          {saving ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
          ) : (
            <Save className="h-3.5 w-3.5" />
          )}
          Save changes
        </button>
      }
    >
      <form onSubmit={onSave} className="space-y-5">
        <Card className="p-5" testid="site-meta-basic">
          <h3 className="text-sm font-semibold text-slate-900 flex items-center gap-2">
            <Globe className="h-4 w-4 text-slate-500" /> Basics
          </h3>
          <p className="text-xs text-slate-500 mt-1">
            What shows up in browser tabs, on the favicon, and as the meta
            description in search results.
          </p>
          <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field
              label="Page title"
              hint="Shown in the browser tab and as the search-result link text."
            >
              <input
                type="text"
                value={meta.page_title || ""}
                onChange={set("page_title")}
                placeholder="The AI Took My Job — So What Do I Do Now?"
                className="mt-1 w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm"
                maxLength={200}
                data-testid="site-meta-title"
              />
            </Field>
            <Field
              label="Theme colour"
              hint="Used by mobile browsers for the address-bar tint."
            >
              <div className="mt-1 flex gap-2 items-center">
                <input
                  type="color"
                  value={meta.theme_color || "#0f172a"}
                  onChange={set("theme_color")}
                  className="h-9 w-12 rounded border border-slate-300 cursor-pointer"
                  data-testid="site-meta-theme-color"
                />
                <input
                  type="text"
                  value={meta.theme_color || ""}
                  onChange={set("theme_color")}
                  placeholder="#0f172a"
                  className="flex-1 rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-mono"
                  maxLength={20}
                />
              </div>
            </Field>
            <Field
              label="Meta description"
              hint="Search engines truncate after ~160 characters."
            >
              <textarea
                rows={3}
                value={meta.meta_description || ""}
                onChange={set("meta_description")}
                placeholder="A calm, practical platform to navigate AI-driven career change…"
                className="mt-1 w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm"
                maxLength={500}
                data-testid="site-meta-description"
              />
              <p className="text-[11px] text-slate-500 mt-1">
                {(meta.meta_description || "").length} / 500 characters
              </p>
            </Field>
            <Field
              label="Keywords"
              hint="Comma-separated. Most search engines ignore this, but some scrapers and crawlers still read it."
            >
              <input
                type="text"
                value={meta.keywords || ""}
                onChange={set("keywords")}
                placeholder="career change, resume builder, AI, australia"
                className="mt-1 w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm"
                maxLength={500}
                data-testid="site-meta-keywords"
              />
            </Field>
          </div>
        </Card>

        <Card className="p-5" testid="site-meta-favicon">
          <h3 className="text-sm font-semibold text-slate-900 flex items-center gap-2">
            <ImageIcon className="h-4 w-4 text-slate-500" /> Favicon
          </h3>
          <p className="text-xs text-slate-500 mt-1">
            The little icon in the browser tab. Use a square image — ICO, PNG
            or SVG. Recommended 32×32 or 64×64 px.
          </p>
          <div className="mt-4 flex flex-col md:flex-row gap-4 md:items-end">
            <div className="flex-1">
              <Field
                label="Favicon URL"
                hint="Paste a full URL, an `/api/uploads/…` path, or use Upload to attach a file."
              >
                <input
                  type="text"
                  value={meta.favicon_url || ""}
                  onChange={set("favicon_url")}
                  placeholder="/favicon.ico"
                  className="mt-1 w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-mono"
                  maxLength={600}
                  data-testid="site-meta-favicon-url"
                />
              </Field>
            </div>
            <div className="flex items-center gap-3">
              {resolve(meta.favicon_url) ? (
                <img
                  src={resolve(meta.favicon_url)}
                  alt="favicon preview"
                  className="h-12 w-12 rounded border border-slate-200 object-contain bg-white"
                  data-testid="site-meta-favicon-preview"
                />
              ) : null}
              <input
                ref={faviconInputRef}
                type="file"
                accept=".ico,.png,.jpg,.jpeg,.webp,.gif,.svg"
                className="hidden"
                onChange={onUpload("favicon")}
                data-testid="site-meta-favicon-file"
              />
              <button
                type="button"
                onClick={() => faviconInputRef.current?.click()}
                disabled={uploading.favicon}
                className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-60"
                data-testid="site-meta-favicon-upload"
              >
                {uploading.favicon ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  <Upload className="h-3.5 w-3.5" />
                )}
                Upload favicon
              </button>
            </div>
          </div>
        </Card>

        <Card className="p-5" testid="site-meta-social">
          <h3 className="text-sm font-semibold text-slate-900 flex items-center gap-2">
            <ExternalLink className="h-4 w-4 text-slate-500" /> Social share preview
          </h3>
          <p className="text-xs text-slate-500 mt-1">
            Controls how the site looks when shared on Facebook, LinkedIn,
            iMessage, X/Twitter, and inside Slack/Discord. Leave Title/Description
            blank to reuse the basics above.
          </p>
          <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="OG title">
              <input
                type="text"
                value={meta.og_title || ""}
                onChange={set("og_title")}
                placeholder="(defaults to Page title)"
                className="mt-1 w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm"
                maxLength={200}
                data-testid="site-meta-og-title"
              />
            </Field>
            <Field label="Twitter card style">
              <select
                value={meta.twitter_card || "summary_large_image"}
                onChange={set("twitter_card")}
                className="mt-1 w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm"
                data-testid="site-meta-twitter-card"
              >
                {TWITTER_CARDS.map((c) => (
                  <option key={c.value} value={c.value}>
                    {c.label}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="OG description">
              <textarea
                rows={2}
                value={meta.og_description || ""}
                onChange={set("og_description")}
                placeholder="(defaults to Meta description)"
                className="mt-1 w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm"
                maxLength={500}
                data-testid="site-meta-og-description"
              />
            </Field>
            <div className="md:col-span-1">
              <Field
                label="OG image URL"
                hint="Recommended size 1200×630 px. Used by all social-card scrapers."
              >
                <input
                  type="text"
                  value={meta.og_image_url || ""}
                  onChange={set("og_image_url")}
                  placeholder="/api/uploads/…  or  https://…"
                  className="mt-1 w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-mono"
                  maxLength={600}
                  data-testid="site-meta-og-image-url"
                />
              </Field>
              <div className="mt-2 flex items-center gap-3">
                {resolve(meta.og_image_url) ? (
                  <img
                    src={resolve(meta.og_image_url)}
                    alt="OG preview"
                    className="h-16 w-28 rounded border border-slate-200 object-cover bg-slate-50"
                    data-testid="site-meta-og-preview"
                  />
                ) : null}
                <input
                  ref={ogInputRef}
                  type="file"
                  accept=".png,.jpg,.jpeg,.webp"
                  className="hidden"
                  onChange={onUpload("og_image")}
                  data-testid="site-meta-og-file"
                />
                <button
                  type="button"
                  onClick={() => ogInputRef.current?.click()}
                  disabled={uploading.og_image}
                  className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-60"
                  data-testid="site-meta-og-upload"
                >
                  {uploading.og_image ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  ) : (
                    <Upload className="h-3.5 w-3.5" />
                  )}
                  Upload OG image
                </button>
              </div>
            </div>
          </div>
        </Card>

        <Card className="p-5" testid="site-meta-seo">
          <h3 className="text-sm font-semibold text-slate-900">
            Search engine controls
          </h3>
          <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field
              label="Canonical URL"
              hint="The preferred URL search engines should index. Leave blank to use the current page URL."
            >
              <input
                type="text"
                value={meta.canonical_url || ""}
                onChange={set("canonical_url")}
                placeholder="https://theaitookmyjob.com.au"
                className="mt-1 w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-mono"
                maxLength={600}
                data-testid="site-meta-canonical"
              />
            </Field>
            <Field label="Robots">
              <select
                value={meta.robots || "index, follow"}
                onChange={set("robots")}
                className="mt-1 w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm"
                data-testid="site-meta-robots"
              >
                {ROBOTS_PRESETS.map((r) => (
                  <option key={r.value} value={r.value}>
                    {r.label}
                  </option>
                ))}
              </select>
            </Field>
          </div>
        </Card>

        <div className="flex justify-end">
          <button
            type="submit"
            disabled={saving}
            className="inline-flex items-center gap-1.5 rounded-lg bg-emerald-600 px-4 py-2.5 text-sm font-medium text-white hover:bg-emerald-700 disabled:opacity-60"
            data-testid="site-meta-save-bottom"
          >
            {saving ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Save className="h-4 w-4" />
            )}
            Save changes
          </button>
        </div>
      </form>
    </AdminLayout>
  );
}
